import type {
  RenterDriverError,
  RenterDriverErrorCode,
  RenterDriverFieldErrors,
} from "./types";

const CODES: readonly RenterDriverErrorCode[] = [
  "renter_drivers_invalid",
  "renter_drivers_required",
  "too_many_renter_drivers",
  "renter_drivers_not_requested",
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

/** The JSON body of a failed request, from either client's error. */
function errorPayload(error: unknown): { status: unknown; body: unknown } {
  if (!isRecord(error)) return { status: null, body: null };

  // `clientWithApiClient` throws `ApiError` (`status`, `data`).
  if ("data" in error) return { status: error.status, body: error.data };

  // The public quote client throws an `Error` carrying `response`.
  if (isRecord(error.response)) {
    return { status: error.response.status, body: error.response.data };
  }

  return { status: null, body: null };
}

const toFieldErrors = (value: unknown): RenterDriverFieldErrors => {
  if (!isRecord(value)) return {};

  const result: RenterDriverFieldErrors = {};
  for (const [field, messages] of Object.entries(value)) {
    if (Array.isArray(messages)) {
      result[field] = messages.filter(
        (message): message is string => typeof message === "string",
      );
    }
  }
  return result;
};

/**
 * Reads a FLEE-442 422 into a typed error, or `null` for any other failure so
 * the caller falls through to its generic handling. Never includes request
 * data: driver details stay out of whatever logs the result.
 */
export function parseRenterDriverError(
  error: unknown,
): RenterDriverError | null {
  const { status, body } = errorPayload(error);
  if (status !== 422 || !isRecord(body)) return null;

  const code = CODES.find((candidate) => candidate === body.code);
  if (!code) return null;

  const errors = isRecord(body.errors) ? body.errors.renter_drivers : undefined;

  const parsed: RenterDriverError = {
    code,
    message: typeof body.error === "string" ? body.error : "",
    driverErrors: Array.isArray(errors) ? errors.map(toFieldErrors) : [],
  };

  if (typeof body.max_additional_drivers === "number") {
    parsed.maxAdditionalDrivers = body.max_additional_drivers;
  }

  return parsed;
}
